import React from 'react';
import { Box } from '@mantine/core';

import { Request } from '../../../codegen/graphql';
import { When } from '../../common';
import { useUser } from '../../../hooks/useUser';
import { QuickActionPanel } from '../pricing-batch-actions-cell/QuickActionPanel';
import { QuickActionForm } from '../pricing-batch-actions-cell/QuickActionForm';
import { QuickActionDeclineForm } from '../pricing-batch-actions-cell/QuickActionDeclineForm';
import type { BatchQuickActionKind } from './BatchExpansionContext';
import { useOptionalPricingBatchStaging } from './PricingBatchStagingContext';

type Props = {
    record: Request;
    collapse: () => void;
    getKindFor: (rowId: string) => BatchQuickActionKind | null;
    closeExpansion: (rowId: string) => void;
};

export function BatchRowExpansionContent({
    record,
    collapse,
    getKindFor,
    closeExpansion,
}: Props) {
    const kind = getKindFor(record.id);
    const { isDivisionManager } = useUser();
    const batchStaging = useOptionalPricingBatchStaging();

    const collapseRow = () => {
        closeExpansion(record.id);
        collapse();
    };

    const cancelPanel = () => {
        const currentKind = getKindFor(record.id);
        batchStaging?.dequeueStagedQuickActionForPanelCancel(
            record.id,
            currentKind,
        );
        collapseRow();
    };

    const finishSuccess = () => {
        collapseRow();
    };

    return (
        <Box className="relative min-w-0 w-full overflow-x-hidden">
            <Box
                aria-hidden
                className="pointer-events-none absolute inset-y-0 left-0 z-0 w-full bg-gray-50"
            />
            <Box className="relative z-[2] min-w-0 w-full max-w-full overflow-x-hidden">
                <When condition={kind === 'approve'}>
                    <QuickActionPanel
                        accentClassName="bg-emerald-500"
                        ariaLabel="Approve request"
                    >
                        <QuickActionForm
                            request={record}
                            verb="approve"
                            onCancel={cancelPanel}
                            onSuccess={finishSuccess}
                        />
                    </QuickActionPanel>
                </When>
                <When condition={kind === 'interim' && !isDivisionManager}>
                    <QuickActionPanel
                        accentClassName="bg-amber-500"
                        ariaLabel="Suggest interim price"
                    >
                        <QuickActionForm
                            request={record}
                            verb="interim"
                            onCancel={cancelPanel}
                            onSuccess={finishSuccess}
                        />
                    </QuickActionPanel>
                </When>
                <When condition={kind === 'decline'}>
                    <QuickActionPanel
                        accentClassName="bg-red-500"
                        ariaLabel="Decline request"
                    >
                        <QuickActionDeclineForm
                            request={record}
                            onCancel={cancelPanel}
                            onSuccess={finishSuccess}
                        />
                    </QuickActionPanel>
                </When>
            </Box>
        </Box>
    );
}
